import { Color } from "@repo/app-types/constants/colors";
import type { PillColor } from "@repo/ui/Pills/pill";
import type React from "react";
import { useMemo } from "react";
import { twMerge } from "tailwind-merge";
import { getScoreProps } from "../insights/assessmentScore.js";

interface AssessmentDistributionBarProps {
  scoreDistribution: Record<number, number> | null;
  isLoading: boolean;
  onScoreClick: (score: number) => void;
}

export const AssessmentDistributionBar: React.FC<
  AssessmentDistributionBarProps
> = ({ scoreDistribution, isLoading, onScoreClick }) => {
  const distribution = useMemo(() => {
    if (!scoreDistribution) return [];

    const total = Object.values(scoreDistribution).reduce(
      (sum, count) => sum + count,
      0,
    );

    return Object.entries(scoreDistribution).map(([score, count]) => ({
      score: parseInt(score),
      count,
      percentage: total ? (count / total) * 100 : 0,
    }));
  }, [scoreDistribution]);

  const getColorClasses = (pillColor: PillColor) => {
    switch (pillColor) {
      case Color.Red:
        return {
          bg: "bg-red-300",
          text: "text-red-900",
          subtext: "text-red-900",
        };
      case Color.Yellow:
        return {
          bg: "bg-yellow-300",
          text: "text-yellow-900",
          subtext: "text-yellow-900",
        };
      case Color.Gray:
        return {
          bg: "bg-gray-300",
          text: "text-foreground",
          subtext: "text-foreground",
        };
      case Color.Lime:
        return {
          bg: "bg-lime-300",
          text: "text-lime-900",
          subtext: "text-lime-900",
        };
      case Color.Green:
        return {
          bg: "bg-green-300",
          text: "text-green-900",
          subtext: "text-green-900",
        };
      case Color.Emerald:
        return {
          bg: "bg-emerald-300",
          text: "text-emerald-900",
          subtext: "text-emerald-900",
        };
      case Color.Amber:
        return {
          bg: "bg-amber-300",
          text: "text-amber-900",
          subtext: "text-amber-900",
        };
      case Color.Orange:
        return {
          bg: "bg-orange-300",
          text: "text-orange-900",
          subtext: "text-orange-900",
        };
      default:
        return {
          bg: "bg-gray-300",
          text: "text-foreground",
          subtext: "text-foreground",
        };
    }
  };

  return (
    <div className="mb-5 w-full">
      <div className={`
          relative h-8 overflow-hidden
          rounded-lg transition-opacity duration-500 ease-in-out
        `}>
        <div className={twMerge(`
          absolute w-full
          transition-opacity duration-500 ease-in-out`, isLoading ? "opacity-100" : "opacity-0")}>
          <div className="h-8 w-full animate-pulse bg-gray-200">
            <div className="shimmer absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          </div>
        </div>

        <div className={twMerge(`
          flex h-8 w-full
          transition-opacity duration-500 ease-in-out`, isLoading ? "opacity-0" : "opacity-100")}>
          {distribution.map(({ score, percentage, count }) => {
            const scoreProps = getScoreProps(score);
            const colors = getColorClasses(scoreProps.color);

            if (percentage === 0) return null;
            return (
              <div
                key={score}
                className={twMerge(
                  "relative flex min-w-[140px] cursor-pointer items-center pl-4",
                  colors.bg,
                )}
                style={{ width: `${percentage || 2}%` }}
                onClick={() => {
                  onScoreClick(score);
                }}
                role="button"
                title={`Show interviews with score ${score} or higher`}
              >
                <div className="flex w-full items-center justify-between space-x-2 whitespace-nowrap">
                  <span
                    className={twMerge("text-sm font-semibold", colors.text)}
                  >
                    {scoreProps.text}
                  </span>
                  <span className={twMerge("pr-4 text-xs", colors.subtext)}>
                    {count} ({Math.round(percentage)}%)
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
